import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { createUseThemedStyles } from '@/hooks';
import { HeaderMenuButton } from '@/components';
import { mediaQueries } from '@/jss';

import { ReactComponent as HackettLogo } from '@/assets/logos/logo-hackett-connect-r-large.svg';

const transitionDuration = 200;

const useStyles = createUseThemedStyles((theme) => ({
	mobileHeader: {
		height: 60,
		display: 'flex',
		padding: '0 15px',
		alignItems: 'center',
		justifyContent: 'space-between',
		'& .hackett-logo': {
			width: 240,
			marginRight: 15,
			color: theme.colors.white,
		},
	},
	mobileNavigation: {
		top: 60,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 7,
		opacity: 0,
		position: 'fixed',
		overflowY: 'auto',
		transform: 'translateY(50px)',
		backgroundColor: theme.colors.white,
		transition: `opacity ${transitionDuration}ms, transform ${transitionDuration}ms`,
		'& ul': {
			margin: 0,
			padding: '5px 0',
			listStyle: 'none',
			'& li': {
				'& a': {
					display: 'block',
					padding: '5px 10px',
					textAlign: 'center',
				},
			},
		},
		[mediaQueries.nav]: {
			display: 'none',
		},
	},
	mobileSearchBar: {
		display: 'flex',
		padding: '15px 0 0',
		justifyContent: 'center',
	},
	'@global': {
		'.mobile-nav-enter': {
			opacity: 0,
			transform: 'translateY(50px)',
		},
		'.mobile-nav-enter-active': {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		'.mobile-nav-enter-done': {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		'.mobile-nav-exit': {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		'.mobile-nav-exit-active': {
			opacity: 0,
			transform: 'translateY(50px)',
		},
	},
}));

interface HeaderMobileProps {
	menuOpen: boolean;
	onMenuButtonClick(menuOpen: boolean): void;
	className?: string;
}

export const HeaderMobile = ({ menuOpen, onMenuButtonClick, className }: HeaderMobileProps) => {
	const classes = useStyles();

	const headerRef = useRef<HTMLElement>(null);
	const mobileNavRef = useRef<HTMLElement>(null);

	const handleWindowResize = useCallback(() => {
		setMobileNavTop();
	}, []);

	/* ----------------------------------------------------------- */
	/* Nav padding for fixed header */
	/* ----------------------------------------------------------- */
	function setMobileNavTop() {
		if (!mobileNavRef.current) {
			return;
		}

		if (!headerRef.current) {
			mobileNavRef.current.style.top = '0px';
			return;
		}

		const headerBottom = headerRef.current.getBoundingClientRect().bottom;
		mobileNavRef.current.style.top = `${headerBottom}px`;
	}

	/* ----------------------------------------------------------- */
	/* Set padding on window resize */
	/* ----------------------------------------------------------- */
	useEffect(() => {
		setMobileNavTop();

		window.addEventListener('resize', handleWindowResize);

		return () => {
			window.removeEventListener('resize', handleWindowResize);
		};
	}, [handleWindowResize]);

	return (
		<header ref={headerRef} className={classNames(classes.mobileHeader, className)}>
			<Link to="/">
				<HackettLogo className="hackett-logo" />
			</Link>
			<HeaderMenuButton
				open={menuOpen}
				onClick={(open) => {
					onMenuButtonClick(open);
				}}
			/>
		</header>
	);
};
